import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { useMatches } from '@tanstack/react-location';
import { VoicemailEventType } from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { useQueryClient, useQuery, InfiniteData } from 'react-query';
import { TagsV2 } from '@frontend/api-tag';
import { VoiceMailboxApi } from '@frontend/api-voicemail-boxes';
import { VoicemailTypes } from '@frontend/api-voicemails';
import { NoAccessEmptyState as NoAccessEmptyStateSvg } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Accordion, Button, Heading, Text, useAlert, useModalControl } from '@frontend/design-system';
import { useVoicemailBoxList } from '../../hooks/use-voicemail-box';
import {
  useVoicemailTagMutation,
  optimisticAddVoicemailTag,
  optimisticRemoveVoicemailTag,
} from '../../hooks/use-voicemail-tag-mutations';
import { transcriptionTrackingId } from '../../pendo-tracking';
import { queryKeys } from '../../query-keys';
import { useVoicemailEventSubscription } from '../../voicemail-event-emitter';
import { VoicemailTableDataRow, VoicemailQueryData } from '../all-calls/types';
import { AddTag } from '../voicemails/voicemail-tag-selector';
import { mutateVoicemailData } from '../voicemails/voicemail-update';
import { Forwardvoicemail } from '../voicemails/voicemails-forward';
import { ForwardVoicemailDynamicData, ForwardVoicemailModalData } from '../voicemails/voicemails-multi.component';
import { TranscriptionContext, VoicemailDetailsAccordionType } from './types';
import { getTranscriptionUniqueKey } from './utils';
import {
  CallDetailsSectionBody,
  ContactActions,
  ContactSection,
  TagsSectionBody,
  TranscriptSectionBody,
  VoicemailMedia,
} from './voicemail-details-panel-sections';

export const defaultForwardVoicemailModalData = {};

export const VoicemailDetailsPanel = () => {
  const { t } = useTranslation('calls');
  const alerts = useAlert();
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const queryClient = useQueryClient();
  const [showNavigation, setShowNavigation] = useState(false);

  const match = useMatches();

  useEffect(() => {
    if (match.some((m) => m.id === '/calls/voicemails')) {
      setShowNavigation(true);
    } else {
      setShowNavigation(false);
    }
  }, [match]);

  const { show, setShow, context, setCleanUp } = useSlidePanelShallowStore<TranscriptionContext>(
    'setShow',
    'context',
    'show',
    'setCleanUp'
  );

  const currentRowIndex =
    context?.rows.findIndex((row) => {
      const transcriptionUniqueKey = getTranscriptionUniqueKey(row);
      return transcriptionUniqueKey === context.currentRowId ?? 0;
    }) ?? 0;

  // Helper function to update panel context without calling setShow directly
  const updatePanelContext = useCallback(
    (updates: Partial<TranscriptionContext>) => {
      if (context) {
        setShow(true, 'voicemailTranscription', {
          ...context,
          ...updates,
        });
      }
    },
    [context, setShow]
  );

  /**
   * Subscribe to query cache updates for latest voicemail data.
   * When prioritizeQueryCache is true:
   * - Component will use and subscribe to the query cache data
   * - Updates to readAt, tags, or other fields will trigger re-renders
   * - Useful when the panel is opened from notifications or external sources
   *
   * When false:
   * - Component will use static data from context.rows
   * - Typically used when opened from the voicemail table view
   */
  const queryKey = useMemo(() => context?.voicemailsQueryKey || ['disabled-query'], [context?.voicemailsQueryKey]);
  const fullQueryKey = useMemo(() => [selectedLocationIds, ...queryKey], [selectedLocationIds, queryKey]);

  const { data: latestData } = useQuery(
    context?.voicemailsQueryKey && context?.prioritizeQueryCache
      ? [selectedLocationIds, ...context.voicemailsQueryKey]
      : ['disabled-query'],
    () => null,
    {
      enabled: !!context?.voicemailsQueryKey && !!context?.prioritizeQueryCache,
      initialData: () =>
        context?.voicemailsQueryKey && context?.prioritizeQueryCache ? queryClient.getQueryData(fullQueryKey) : null,
    }
  );

  const row = context?.prioritizeQueryCache
    ? (latestData as unknown as VoicemailTableDataRow)
    : context?.rows[currentRowIndex];

  const transcriptionUniqueKey = getTranscriptionUniqueKey(row);

  // Subscribe to voicemail update events to refresh panel data
  useVoicemailEventSubscription(
    'voicemail:update',
    useCallback(
      ({ event }) => {
        if (!context || !row || !context.voicemailsQueryKey) return;

        // Check if the event is relevant to the current voicemail
        const isCurrentVoicemail = event.voicemail?.id === row.voicemailId || event.voicemail?.id === row.channelId;

        if (isCurrentVoicemail) {
          // Get current voicemail data from query cache
          const currentData = queryClient.getQueryData<InfiniteData<VoicemailQueryData>>(fullQueryKey);

          if (currentData) {
            // Process the voicemail data using mutateVoicemailData
            const updatedData = mutateVoicemailData({
              voicemailData: currentData,
              voicemailId: event.voicemail?.id || '',
              pageSize: context.pageSize ?? 25,
              callbackFn: ({ voicemailRow }) => {
                if (voicemailRow && event.voicemail) {
                  // Update the voicemail row based on event type
                  switch (event.type) {
                    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_READ:
                      voicemailRow.readAt = '0';
                      break;
                    case VoicemailEventType.VOICEMAIL_EVENT_TYPE_UNREAD:
                      voicemailRow.readAt = undefined;
                      break;
                    // Add other event types as needed
                  }
                }
              },
            });

            if (updatedData) {
              // Update the query cache with the processed data
              queryClient.setQueryData(fullQueryKey, updatedData);

              // Update the panel context with the new rows
              updatePanelContext({
                rows: updatedData.pages.flatMap((page) => page.rows || []),
              });
            }
          }
        }
      },
      [context, row, queryClient, fullQueryKey, updatePanelContext]
    )
  );

  // Subscribe to voicemail tag events to refresh panel data
  useVoicemailEventSubscription(
    'voicemail:tag',
    useCallback(
      ({ event }) => {
        if (!context || !row || !context.voicemailsQueryKey) return;

        // Check if the event is relevant to the current voicemail
        const isCurrentVoicemail =
          event.voicemailMessageId === row.voicemailId || event.voicemailMessageId === row.channelId;

        if (isCurrentVoicemail) {
          // For tag events, invalidate the query to refetch the latest tag data
          queryClient.invalidateQueries([selectedLocationIds, ...context.voicemailsQueryKey]);
        }
      },
      [context, row, selectedLocationIds, queryClient]
    )
  );

  const [openedAccordionId, setOpenedAccordionId] = useState<VoicemailDetailsAccordionType>(
    context?.sectionFocus || 'transcript'
  );

  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const [forwarVoicemailModalData, setForwardVoicemailModalData] = useState<ForwardVoicemailModalData>(
    defaultForwardVoicemailModalData as ForwardVoicemailModalData
  );
  const doubleActionControl = useModalControl();

  useLayoutEffect(() => {
    if (show && !!context?.sectionFocus && openedAccordionId !== context.sectionFocus) {
      setOpenedAccordionId(context.sectionFocus);
    }
  }, [openedAccordionId, context?.sectionFocus, show]);

  const { data: transcription, status: transcriptionStatus } = useScopedQuery({
    queryKey: queryKeys.voicemailTranscript(row?.channelId ?? ''),
    queryFn: async () => {
      const response = await VoiceMailboxApi.getTranscription(row?.channelId ?? '');
      return response;
    },
    enabled: !!row,
  });

  const { data: tagData } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: row?.tags?.length === 0,
    },
  });

  const mappedTags =
    tagData?.tags.reduce((acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    }, {} as Record<string, (typeof tagData)['tags'][number]>) ?? {};

  const assignedTags = !!row?.tags?.length ? row.tags : row?.tagIds?.map((tagId: string) => mappedTags[tagId]) || [];

  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCleanUp(() => {
      if (context) {
        context.toggleRowSelected(transcriptionUniqueKey, false, currentRowIndex);
      }
    });
  }, [context, currentRowIndex, setCleanUp, transcriptionUniqueKey]);

  /**
   * This effect is used to fetch the next page of voicemails when the user reaches the end of the current page of results.
   */
  useEffect(() => {
    if (context && currentRowIndex + 1 >= context.rows.length) {
      context.getNextPage?.().then((res) => {
        updatePanelContext({
          rows: res.data?.pages.flatMap((page) => page.rows || []) ?? [],
          getNextPage: res.data?.pages.at(-1)?.meta.hasNext ? context.getNextPage : undefined,
        });
      });
    }
  }, [currentRowIndex, context?.rows.length, context, updatePanelContext]);

  const { addTag, removeTag } = useVoicemailTagMutation({
    queryKey: context?.voicemailsQueryKey ?? [],
    voicemailId: row?.voicemailId ?? '',
    optimisticRemoveTag: (args) => {
      // Implement optimistic UI update for removing a tag
      const newData = optimisticRemoveVoicemailTag({
        oldData: args.oldData,
        tag: args.tag,
        voicemailId: row?.voicemailId ?? '',
      });
      if (newData) {
        // Update the panel context with the new data
        updatePanelContext({
          rows: newData.pages.flatMap((page) => page.rows || []),
        });
        return newData;
      }
      return;
    },
    optimisticAddTag: (args) => {
      // Implement optimistic UI update for adding a tag
      const newData = optimisticAddVoicemailTag({
        oldData: args.oldData,
        tag: args.tag,
        voicemailId: row?.voicemailId ?? '',
      });

      if (newData) {
        // Update the panel context with the new data
        updatePanelContext({
          rows: newData.pages.flatMap((page) => page.rows || []),
        });
        return newData;
      }
      return;
    },
    onSuccess: ({ op }) => {
      if (op === 'add') {
        alerts.success(t('Tag added successfully.'));
      }
      if (op === 'remove') {
        alerts.success(t('Successfully removed tag.'));
      }
    },
    onFailure: ({ op, oldData }) => {
      if (op === 'add') alerts.error(t('Failed to add tag.'));
      if (op === 'remove') alerts.error(t('Failed to remove tag.'));

      // If the operation failed, we can revert the optimistic update by resetting the rows
      if (oldData) {
        updatePanelContext({
          rows: oldData.pages.flatMap((page) => page.rows || []),
        });
      }
    },
  });

  if (!context) {
    return null;
  }

  const openForwardVoicemailModal = (data: ForwardVoicemailDynamicData) => {
    setForwardVoicemailModalData({
      ...data,
      voicemailBoxList: voicemailBoxList || [],
      onDelete: context.deleteVoicemail,
    });
    doubleActionControl.openModal();
  };

  const handleUpdateReadStatus = ({ markIsRead, voicemailId }: { markIsRead: boolean; voicemailId: string }) => {
    context.updateReadStatus?.({
      index: currentRowIndex,
      status: markIsRead ? VoicemailTypes.ViewStatus.VIEW_STATUS_READ : VoicemailTypes.ViewStatus.VIEW_STATUS_UNREAD,
      voicemailId: voicemailId,
    });
  };

  return (
    <div
      ref={panelRef}
      style={{ display: 'grid', gridTemplateRows: 'auto 1fr auto', height: '100%', padding: theme.spacing(3) }}
    >
      <Forwardvoicemail modalProps={doubleActionControl.modalProps} forwardData={forwarVoicemailModalData} />

      <header
        style={{
          display: 'grid',
          alignItems: 'center',
          padding: theme.spacing(1),
          gridTemplateColumns: '1fr max-content',
        }}
      >
        <Heading level={2} css={{ margin: 0 }}>
          {t('Voicemail Details')}
        </Heading>
        <Button
          css={{
            justifySelf: 'end',
            width: 'max-content',
          }}
          variant='secondary'
          label={t('Close panel')}
          iconName='x'
          size='large'
          onClick={() => {
            setShow(false);
            context.toggleRowSelected(transcriptionUniqueKey, false, currentRowIndex);
          }}
        />
      </header>

      {row && (
        <div style={{ overflow: 'auto' }}>
          <ContactSection
            contact={{
              id: row.contactId,
              firstName: row.firstName,
              lastName: row.lastName,
              profileName: isNaN(Number(row.contactName)) ? row.contactName : t('Unknown'),
              defaultName: t('Unknown'),
              number: row.contactNumber,
            }}
            locationId={row.locationIds?.[0] ?? ''}
            isMarked={!row.readAt}
          >
            <ContactActions
              contact={{ name: row.contactName, id: row.contactId, number: row.contactNumber }}
              locationId={row.locationIds?.[0] ?? ''}
              mediaFilePath={row.mediaFilePath}
              date={row.dateTime}
              message={row}
              updateReadStatus={(markIsRead) => handleUpdateReadStatus({ markIsRead, voicemailId: row.voicemailId })}
              openForwardVoicemailModal={() => {
                openForwardVoicemailModal({
                  currentVoicemailBoxId: row.mailboxId,
                  mediaId: row.mediaId,
                  mediaPath: row.mediaFilePath,
                  currentVoicemailBoxName: row.voiceMailBoxName,
                  vmId: row.voicemailId,
                  rowIndex: currentRowIndex,
                });
              }}
              isVmBoxRestricted={!context.userHasAccess}
            />
          </ContactSection>

          {!!context.userHasAccess && (
            <>
              <VoicemailMedia
                message={row}
                mediaFilePath={row.mediaFilePath}
                updateReadStatus={() => handleUpdateReadStatus({ markIsRead: true, voicemailId: row.channelId })}
              />

              <Accordion
                variant='blank'
                controlledValue={openedAccordionId}
                onChange={(value) => {
                  setOpenedAccordionId(value as VoicemailDetailsAccordionType);
                  updatePanelContext({
                    sectionFocus: value as VoicemailDetailsAccordionType,
                  });
                }}
                padding={0}
                css={css`
                  padding: ${theme.spacing(1)};
                  > div:not(:last-child) {
                    margin-bottom: ${theme.spacing(2)};
                  }
                `}
              >
                <Accordion.Item title={t('Transcript')} value='transcript' css={styles.accordionItemStyle}>
                  <Accordion.Header
                    title={t('Transcript')}
                    css={styles.accordionHeaderStyle(openedAccordionId, 'transcript')}
                  />
                  <Accordion.Body>
                    <TranscriptSectionBody
                      status={transcriptionStatus}
                      message={row}
                      transcription={transcription}
                      updateReadStatus={() =>
                        handleUpdateReadStatus({ markIsRead: true, voicemailId: row.voicemailId })
                      }
                      isOpen={openedAccordionId === 'transcript'}
                    />
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item title={t('About this voicemail')} value='details' css={styles.accordionItemStyle}>
                  <Accordion.Header
                    title={t('About this voicemail')}
                    css={styles.accordionHeaderStyle(openedAccordionId, 'details')}
                  />
                  <Accordion.Body>
                    <CallDetailsSectionBody
                      mailboxName={row.voiceMailBoxName}
                      date={row.dateTime}
                      locationId={row.locationIds?.[0]}
                    />
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item title={t('Tags')} value='tags' css={styles.accordionItemStyle}>
                  <Accordion.Header css={styles.accordionHeaderStyle(openedAccordionId, 'tags')}>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: ${theme.spacing(0.5)};
                        `}
                      >
                        <Text
                          css={css`
                            font-size: ${theme.fontSize(18)};
                          `}
                          weight='bold'
                        >
                          {t('Tags')}
                        </Text>
                        <Text size='small' color='subdued'>
                          {assignedTags.length > 0 ? assignedTags.length : ''}
                        </Text>
                      </div>
                      <AddTag existingTagIds={assignedTags.map((tag) => tag.id)} onApplyTag={addTag} />
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <TagsSectionBody tags={assignedTags} onApplyTag={addTag} onRemoveTag={removeTag} />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          )}

          {!context.userHasAccess && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: `${theme.spacing(4)}`,
              }}
            >
              <NoAccessEmptyStateSvg css={{ width: 375, height: 375 }} />
              <Text
                textAlign='center'
                css={{ color: theme.colors.neutral50, padding: theme.spacing(0, 1.5), width: theme.spacing(42) }}
              >
                {!!row?.voiceMailBoxName
                  ? t(
                      `This voicemail is in a restricted voicemail box, {{vmBoxName}}. Contact your admin if you need access.`,
                      { vmBoxName: row?.voiceMailBoxName }
                    )
                  : t('This voicemail is in a restricted voicemail box. Contact your admin if you need access.')}
              </Text>
            </div>
          )}
        </div>
      )}

      {!!context.userHasAccess && (
        <div
          style={{
            display: 'grid',
            padding: theme.spacing(2, 1, 1),
            justifyContent: 'end',
            gridTemplateColumns:
              '[first-start] max-content [first-end] ' +
              '1fr' + // gap
              '[second-start] max-content [second-end] ' +
              `${theme.spacing(1)} ` + // gap
              '[third-start] max-content [third-end]',
          }}
        >
          <Button
            trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'delete' })}
            size='large'
            destructive
            variant='tertiary'
            iconName='trash'
            css={{ gridColumn: 'first-start / first-end' }}
            onClick={() => {
              if (!context || !row) return;
              context.deleteVoicemail({
                index: currentRowIndex,
                voicemailId: row?.voicemailId,
                mailboxId: row?.mailboxId,
              });
              if (currentRowIndex + 1 >= context?.rows.length) {
                context.toggleRowSelected(transcriptionUniqueKey, false, currentRowIndex);
                setShow(false);
                return;
              }
              // Navigate to next voicemail after deletion
              const nextRowId = getTranscriptionUniqueKey(context.rows[currentRowIndex + 1]);
              context.toggleRowSelected(nextRowId, true, currentRowIndex + 1);
              updatePanelContext({
                currentRowId: nextRowId,
              });
            }}
          >
            {t('Delete Voicemail')}
          </Button>
          {showNavigation && (
            <>
              <Button
                variant='secondary'
                trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'previous' })}
                size='large'
                css={{ width: 'auto', marginLeft: 'auto', gridColumn: 'second-start / second-end' }}
                disabled={currentRowIndex - 1 < 0}
                onClick={() => {
                  const nextRowId = getTranscriptionUniqueKey(context.rows[currentRowIndex - 1]);
                  context.toggleRowSelected(nextRowId, true, currentRowIndex - 1);
                  updatePanelContext({
                    currentRowId: nextRowId,
                  });
                }}
              >
                {t('Previous')}
              </Button>

              <Button
                variant='secondary'
                trackingId={transcriptionTrackingId({ subComponent: 'panel', context: 'next' })}
                size='large'
                css={{ width: 'auto', gridColumn: 'third-start / third-end' }}
                disabled={currentRowIndex + 1 >= context?.rows.length}
                onClick={() => {
                  const nextRowId = getTranscriptionUniqueKey(context.rows[currentRowIndex + 1]);
                  context.toggleRowSelected(nextRowId, true, currentRowIndex + 1);
                  updatePanelContext({
                    currentRowId: nextRowId,
                  });
                }}
              >
                {t('Next')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  accordionHeaderStyle: (
    openedAccordionId: VoicemailDetailsAccordionType,
    currentAccordionType: VoicemailDetailsAccordionType
  ) => css`
    background-color: ${openedAccordionId === currentAccordionType ? theme.colors.neutral5 : 'transparent'};
    border-bottom: none;
    padding: ${theme.spacing(2, 0, 2, 2)};
  `,

  accordionItemStyle: css`
    border-radius: ${theme.spacing(1)};
    border: 1px solid ${theme.colors.neutral20};
    :last-child {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }
  `,
};
